import {Box, Grid, Paper, Typography} from "@mui/material"
import ButtonWithIcon from "../../../../components/Button/Button"
import GridRadioButton from "../../../../components/radio-button/grid-radio-button"
import React, {useEffect, useMemo, useRef, useState} from "react"
import {ReactComponent as ChatIcon} from "src/v2/assets/icons/chat-icon.svg"
import {ReactComponent as CheckIcon} from "src/v2/assets/icons/check-icon.svg"
import {ReactComponent as TrashIcon} from "src/v2/assets/icons/trash-icon.svg"
import {ReactComponent as FileIcon} from "src/v2/assets/icons/file-icon.svg"
import {ReactComponent as PlusSquareIcon} from "src/v2/assets/icons/plus-square-icon.svg"
import {dispatchSnackbar} from "../../../../components/SnackBar/snackbar-bus"
import EditarPromptModal from "./editar-prompt-modal"
import PromptService from "../../../../../services/prompt-service"
import MotorService from "../../../../../services/motor-service"
import PropTypes from "prop-types"

const TIPO_DOCUMENTO_MAP = [
    {value: "TermoSecuritizacao", label: "Termo de securitização"},
    {value: "TelaCetipCorporate", label: "Tela CETIP corporate"},
    {value: "TelaCetipIF", label: "Tela CETIP IF"},
    {value: "EscrituraDebenture", label: "Escritura de Debênture"},
    {value: "OutrasEscrituras", label: "Outras escrituras"},
    {value: "FluxoEventos", label: "Fluxo de eventos"},
]

const OcrPromptComponent = ({cetip, versaoDocumento, loading, source, callback}) => {
    const [listaPromptsBackend, setListaPromptsBackend] = useState([])
    const [promptAtual, setPromptAtual] = useState("")
    const [tipoSelecionado, setTipoSelecionado] = useState("")
    const [ocrFile, setOcrFile] = useState(null)
    const fileInputRef = useRef(null)
    const [modalEditarOpen, setModalEditarOpen] = useState(false)
    const [promptFinalEnvio, setPromptFinalEnvio] = useState("")
    const handleFecharModal = () => {
        setModalEditarOpen(false)
    }
    const promptService = useMemo(() => new PromptService(), [])
    const motorService = useMemo(() => new MotorService(), [])

    const handleSalvarModal = (novoPrompt) => {
        setPromptFinalEnvio(novoPrompt)
        setModalEditarOpen(false)
        dispatchSnackbar("Prompt atualizado para envio!", "success")
    }

    useEffect(() => {
        promptService.getPrompts()
            .then((response) => {
                const resultados = response.body?.results || []
                setListaPromptsBackend(resultados)
            })
            .catch(() => {
                dispatchSnackbar("Erro ao carregar tipos de documentos OCR", "error")
            })
    }, [promptService])

    // estilo para o upload
    const dashedAreaStyle = {
        width: '100%',
        border: '2px dashed #64748b',
        borderRadius: '5px',
        padding: '24px 70px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        transition: 'all 0.2s'
    }

    const selectedFileAreaStyle = {
        backgroundColor: 'background.tertiary',
        border: '1px solid #334155',
        padding: '20px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'space-between',
        marginTop: '10px',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        flexWrap: 'wrap',
    }

    const handleRadioChange = (event) => {
        const valorRadio = event.target.value
        setTipoSelecionado(valorRadio)
        setPromptFinalEnvio("")

        const promptEncontrado = listaPromptsBackend.find(
            item => item.TipoDocumento === valorRadio
        )
        if (promptEncontrado) {
            setPromptAtual(promptEncontrado.Prompt)
            return
        }

        setPromptAtual("")
    }

    const handleAbrirModal = () => {
        if (!tipoSelecionado) {
            dispatchSnackbar("Selecione um tipo de documento primeiro.", "warning")
            return
        }
        if (!promptAtual && !promptFinalEnvio) {
            dispatchSnackbar("Não existe prompt base para editar.", "warning")
            return
        }
        setModalEditarOpen(true)
    }

    const handleFileSelect = (event) => {
        const file = event.target.files[0]
        if (file) {
            setOcrFile(file)
        }
    }

    const handleDrop = (event) => {
        event.preventDefault()
        const file = event.dataTransfer.files[0]
        if (file) {
            setOcrFile(file)
        }
    }

    const handleRemoveFile = () => {
        setOcrFile(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    const handleTriggerFileSelect = () => {
        fileInputRef.current.click()
    }

    const handleUploadOCR = () => {
        if (!ocrFile) {
            dispatchSnackbar("Selecione um arquivo para enviar.", "warning")
            return
        }

        if (!cetip) {
            dispatchSnackbar("Informe o CETIP no filtro superior.", "warning")
            return
        }
        if (!versaoDocumento) {
            dispatchSnackbar("Informe a Versão do Documento no filtro superior.", "warning")
            return
        }

        if (!tipoSelecionado) {
            dispatchSnackbar("Selecione o Tipo de Documento.", "warning")
            return
        }

        const promptParaEnviar = promptFinalEnvio || promptAtual

        const formData = new FormData()
        formData.append("Arquivo", ocrFile)
        formData.append("CETIP", cetip)
        formData.append("VersaoDocumento", versaoDocumento)
        formData.append("TipoDocumento", tipoSelecionado)
        formData.append("Origem", source)

        if (promptParaEnviar) {
            formData.append("Prompt", promptParaEnviar)
        }

        loading(true)
        motorService.postUploadOcr(formData)
            .then(() => {
                dispatchSnackbar("Arquivo enviado com sucesso!", "success")

                setOcrFile(null)
                setTipoSelecionado("")
                setPromptFinalEnvio("")
                if (fileInputRef.current) fileInputRef.current.value = ""
                callback()
            })
            .catch((err) => {
                console.error("Erro ao enviar arquivo OCR:", err)

                const body = err?.response?.body ?? err?.body
                const detail =
                    typeof body === "string"
                        ? body
                        : body != null
                            ? JSON.stringify(body)
                            : (err?.message || "Detalhes indisponíveis")

                dispatchSnackbar(`Erro ao enviar arquivo OCR: ${detail}`, "error")
            })
            .finally(() => {
                loading(false)
            })
    }


    return (
        <>
            <Paper sx={{p: 3, height: '100%', borderRadius: 0}}>
                <Grid container direction="column" spacing={2}>
                    <Grid item container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h6" component="span" color="common.white">
                                LER DOCUMENTO OCR
                            </Typography>
                        </Grid>
                        <Grid item>
                            <ButtonWithIcon
                                onClick={handleAbrirModal}
                                icon={<ChatIcon/>}
                            >
                                EDITAR PROMPT
                            </ButtonWithIcon>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} xl={12} sx={{width: '100%'}}>
                        <GridRadioButton
                            defaultRadioValue={tipoSelecionado}
                            onChange={handleRadioChange}
                            direction={"column"}
                            columns={3}
                            itemsPerColumn={2}
                            title="Tipo de Documento"
                            radioButtonItems={TIPO_DOCUMENTO_MAP}
                        />
                    </Grid>
                    <br/>
                    <Grid item xs={12} sx={{mt: 2}}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            style={{display: "none"}}
                            accept=".pdf,.png,.jpg,.jpeg"
                        />

                        {!ocrFile ? (
                            <Box
                                sx={dashedAreaStyle}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <ButtonWithIcon
                                    onClick={handleTriggerFileSelect}
                                    icon={<PlusSquareIcon/>}
                                >
                                    SELECIONAR ARQUIVO
                                </ButtonWithIcon>
                                <Typography variant="body2" sx={{mt: 1, color: '#94a3b8'}}>
                                    Ou arraste e solte os arquivos aqui.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={selectedFileAreaStyle}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexGrow: 1,
                                    minWidth: 0,
                                    mr: 1
                                }}>
                                    <FileIcon sx={{color: 'icon.main'}}/>
                                    <Typography variant="body1" sx={{
                                        color: '#fff',
                                        fontWeight: 400,
                                    }}>
                                        {ocrFile.name}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex', gap: 1, flexShrink: 0,
                                    justifyContent: 'flex-end', flexGrow: {xs: 1, md: 0}
                                }}>
                                    <ButtonWithIcon
                                        onClick={handleUploadOCR}
                                        icon={<CheckIcon sx={{fontSize: 18}}/>}
                                        style={{
                                            backgroundColor: 'transparent.white10',
                                            borderColor: 'primary.main3',
                                            color: '#e2e8f0',
                                            padding: '6px 16px',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        ENVIAR ARQUIVO
                                    </ButtonWithIcon>

                                    <ButtonWithIcon
                                        onClick={handleRemoveFile}
                                        icon={<TrashIcon sx={{fontSize: 18}}/>}
                                        style={{
                                            backgroundColor: 'transparent.white10',
                                            borderColor: 'primary.main3',
                                            color: '#e2e8f0',
                                            padding: '6px 16px',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        EXCLUIR ARQUIVO
                                    </ButtonWithIcon>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            <EditarPromptModal
                open={modalEditarOpen}
                onClose={handleFecharModal}
                onConfirm={handleSalvarModal}
                textoInicial={promptFinalEnvio || promptAtual}
            />
        </>
    )
}

OcrPromptComponent.propTypes = {
    cetip: PropTypes.string.isRequired,
    versaoDocumento: PropTypes.string.isRequired,
    loading: PropTypes.func.isRequired,
    source: PropTypes.oneOf(["parametros", "caracteristicas"]).isRequired,
    callback: PropTypes.func.isRequired,
}

export default OcrPromptComponent